import type { PlaybackSpec, SoundId } from '@audigoal/core';

/**
 * Pure timing maths for playback: beats to seconds, swing, count-in, strums.
 * Nothing here touches Web Audio, so it is tested exhaustively in Node.
 */

export interface ScheduledNote {
  /** Seconds from the start of playback (after any count-in). */
  time: number;
  /** Seconds. */
  duration: number;
  midi: number;
  /** 0..1 */
  velocity: number;
  sound: SoundId;
  /** Beat position in the spec, for cursor callbacks. */
  beat: number;
  /** Cents to bend this note by; see `PlaybackEvent.detuneCents`. */
  detuneCents?: number;
}

export interface ScheduledTick {
  time: number;
  accent: boolean;
  /** Negative beats are the count-in. */
  beat: number;
}

export interface ExpandedPlayback {
  notes: ScheduledNote[];
  ticks: ScheduledTick[];
  /** Seconds from start to the end of the last note, including the count-in. */
  totalSeconds: number;
  /** Seconds of count-in before beat 0. */
  offsetSeconds: number;
  secondsPerBeat: number;
}

export function secondsPerBeat(tempo: number): number {
  if (!(tempo > 0)) throw new Error(`Tempo must be positive, got ${tempo}`);
  return 60 / tempo;
}

/**
 * Swing delays every off-beat eighth (a beat position whose fraction is 0.5)
 * by `swing * (eighth / 3)`: at 0.66 the pair becomes a triplet feel.
 */
export function swingDelay(beat: number, swing: number, spb: number): number {
  if (swing <= 0) return 0;
  const frac = beat - Math.floor(beat);
  if (Math.abs(frac - 0.5) > 1e-6) return 0;
  return swing * (spb / 2 / 3);
}

/** Order chord tones for a strum: the spec order is physical string order. */
export function strumOrder(midis: readonly number[], direction: 'down' | 'up'): number[] {
  return direction === 'down' ? [...midis] : [...midis].reverse();
}

export function expandPlayback(spec: PlaybackSpec): ExpandedPlayback {
  const spb = secondsPerBeat(spec.tempo);
  const countIn = spec.countIn ?? 0;
  const offsetSeconds = countIn * spb;
  const notes: ScheduledNote[] = [];
  const ticks: ScheduledTick[] = [];

  for (let i = 0; i < countIn; i += 1) {
    ticks.push({ time: i * spb, accent: i === 0, beat: i - countIn });
  }

  let lastBeatEnd = 0;
  for (const event of spec.events) {
    const velocity = event.vel ?? 0.8;
    const base = offsetSeconds + event.at * spb + swingDelay(event.at, spec.swing ?? 0, spb);
    const duration = Math.max(0.05, event.dur * spb);
    lastBeatEnd = Math.max(lastBeatEnd, event.at + event.dur);
    const spread =
      spec.strum !== undefined && event.midi.length > 1 ? spec.strum.spreadMs / 1000 : 0;
    const ordered =
      spec.strum !== undefined ? strumOrder(event.midi, spec.strum.direction) : event.midi;
    ordered.forEach((midi, index) => {
      notes.push({
        time: base + index * spread,
        duration: Math.max(0.05, duration - index * spread),
        midi,
        velocity,
        sound: spec.instrument,
        beat: event.at,
        ...(event.detuneCents !== undefined ? { detuneCents: event.detuneCents } : {}),
      });
    });
  }

  if (spec.metronome === true) {
    for (let beat = 0; beat < Math.ceil(lastBeatEnd); beat += 1) {
      ticks.push({ time: offsetSeconds + beat * spb, accent: beat % 4 === 0, beat });
    }
  }

  notes.sort((a, b) => a.time - b.time);
  ticks.sort((a, b) => a.time - b.time);
  const totalSeconds = Math.max(
    offsetSeconds + lastBeatEnd * spb,
    ...notes.map((n) => n.time + n.duration),
  );
  return { notes, ticks, totalSeconds, offsetSeconds, secondsPerBeat: spb };
}

/** The beat at a given playback time, for the cursor. */
export function beatAt(seconds: number, expanded: ExpandedPlayback): number {
  return (seconds - expanded.offsetSeconds) / expanded.secondsPerBeat;
}

/** MIDI to frequency, A4 = 440. */
export function midiToHz(midi: number, a4Hz = 440): number {
  return a4Hz * 2 ** ((midi - 69) / 12);
}

/** Fractional MIDI number of a frequency. Returns NaN for a non-positive input. */
export function hzToMidi(hz: number, a4Hz = 440): number {
  if (hz <= 0) return NaN;
  return 69 + 12 * Math.log2(hz / a4Hz);
}

/** Signed cents from `hz` to the nearest semitone, in [-50, 50). */
export function centsOffPitch(hz: number, a4Hz = 440): number {
  const midi = hzToMidi(hz, a4Hz);
  return (midi - Math.round(midi)) * 100;
}
